import { useMemo, useState } from 'react';
import type { ScenarioInput, ScenarioResult } from '@exigo/shared';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { EngagementRow } from './EngagementRow';
import { NotRelevantList } from './NotRelevantList';
import { buildExportHtml, exportFileName } from './buildExportHtml';
import { downloadWordExport, printExport } from './exportActions';
import styles from './ResultsList.module.css';

interface ResultsListProps {
  result: ScenarioResult;
  fx: number;
  anyInput: boolean;
  /** Needed by the export document for segment, market, term and pre-qualification. */
  input: ScenarioInput;
  areaName: string;
  customerName: string | null;
  guideLabel: string;
}

/**
 * Renders the sections in the same order and grouping as the legacy results
 * list: recommended, stackable, alternatives, "one capability away", "pays more
 * but blocked", then everything else blocked (split from what's simply not
 * relevant to the workloads entered).
 */
export function ResultsList({
  result,
  fx,
  anyInput,
  input,
  areaName,
  customerName,
  guideLabel,
}: ResultsListProps): React.JSX.Element {
  const [includeNearMiss, setIncludeNearMiss] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /** Built fresh on each click so the document always reflects what is on screen. */
  function makeHtml(): { html: string; preparedOn: Date } {
    const preparedOn = new Date();
    return {
      preparedOn,
      html: buildExportHtml({
        result,
        input,
        areaName,
        customerName,
        guideLabel,
        preparedOn,
        includeNearMiss,
      }),
    };
  }

  function handlePdf(): void {
    const { html } = makeHtml();
    void printExport(html).then((ok) => {
      setExportError(ok ? null : 'Printing was blocked by the browser. Use Export Word instead.');
    });
  }

  function handleWord(): void {
    const { html, preparedOn } = makeHtml();
    downloadWordExport(html, exportFileName(areaName, preparedOn, 'doc'));
    setExportError(null);
  }

  // Shown by default, as in the guide: the blocked list is the escalation list,
  // and hiding it behind a click made the reason a deal does not qualify the
  // least visible thing on the page.
  const [showBlocked, setShowBlocked] = useState(true);

  const byKey = useMemo(() => new Map(result.rows.map((r) => [r.key, r])), [result.rows]);

  const recommended = result.rows.filter((r) => r.eligible && r.pick === 'recommended');
  const stackable = result.rows.filter((r) => r.eligible && r.pick === null);
  const alternatives = result.rows.filter((r) => r.eligible && r.pick === 'alternative');
  const blocked = result.rows.filter((r) => !r.eligible);
  const notRelevant = blocked.filter((r) => !r.relevant);
  const realBlocked = blocked.filter((r) => r.relevant);

  const higherBlocked = result.higherBlockedKeys.map((k) => byKey.get(k)).filter((r) => r !== undefined);
  const oneCapabilityAway = result.oneCapabilityAwayKeys.map((k) => byKey.get(k)).filter((r) => r !== undefined);

  const nothingEligible =
    recommended.length === 0 && stackable.length === 0 && alternatives.length === 0;

  const eligibleCount = recommended.length + stackable.length + alternatives.length;

  return (
    <div>
      {/* The export bar. Sits above the results, as in the guide, so the count
          and the two exports read as one line. */}
      <div className={styles.exportBar}>
        <span className={styles.eligibleLabel}>Eligible — {eligibleCount}</span>
        <span className={styles.exportRule} aria-hidden="true" />
        <Checkbox checked={includeNearMiss} onChange={setIncludeNearMiss}>
          Include near-miss list
        </Checkbox>
        <Button size="sm" onClick={handlePdf}>
          Export PDF
        </Button>
        <Button size="sm" onClick={handleWord}>
          Export Word
        </Button>
      </div>

      {exportError && <div className={styles.exportError}>{exportError}</div>}

      {/*
        The empty state is rendered inline rather than returned early. Returning
        early also removed the blocked and not-relevant lists, which is exactly
        the moment they matter most: with nothing eligible, the only useful thing
        on screen is which gate is closing each engagement.
      */}
      {nothingEligible && (
        <EmptyState
          title={anyInput ? 'Nothing clears every gate yet' : 'Enter the deal to begin'}
          body={
            anyInput
              ? 'The blocked list below names the specific gate closing each engagement, and the not-relevant list shows what does not apply to this deal at all.'
              : 'Nothing is assumed about pricing or scope. Put consumption against the workloads above and the tool will name the engagement you should nominate, rather than listing everything you might theoretically claim.'
          }
        />
      )}
      {recommended.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <p className={styles.sectionTitle}>
              Recommended nomination{recommended.length === 1 ? '' : 's'} — {recommended.length}
              {alternatives.length > 0 ? ` (${String(alternatives.length)} alternative${alternatives.length === 1 ? '' : 's'})` : ''}
            </p>
          </div>
          <div className={styles.recoWrap}>
            {recommended.map((r) => (
              <EngagementRow key={r.key} row={r} fx={fx} />
            ))}
          </div>
          {result.recommendationReasons.length > 0 && (
            <div className={styles.reasons}>
              <p className={styles.sectionTitle} style={{ margin: 0 }}>
                Why this engagement
              </p>
              <ul>
                {result.recommendationReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {stackable.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Stacks on top — claimable in addition — {stackable.length}</p>
          {stackable.map((r) => (
            <EngagementRow key={r.key} row={r} fx={fx} />
          ))}
        </div>
      )}

      {alternatives.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Alternatives — same consumption, choose one — {alternatives.length}</p>
          <p className={styles.sectionNote} style={{ marginBottom: 'var(--space-2)' }}>
            Mutually exclusive with the recommendation above; not included in the claimable total.
          </p>
          {alternatives.map((r) => (
            <EngagementRow key={r.key} row={r} fx={fx} />
          ))}
        </div>
      )}

      {oneCapabilityAway.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>One capability away — {oneCapabilityAway.length}</p>
          {oneCapabilityAway.map((r) => (
            <div key={r.key}>
              <EngagementRow row={r} fx={fx} />
              {/* Which capability, specifically. Without this the list says an
                  engagement is close but not what would unlock it, which is the
                  whole point of showing it. Phrasing matches the engine's own
                  capability gate detail — these are alternative routes, not a
                  set that must all be held. */}
              {r.capabilityDetail.length > 0 && (
                <p className={styles.needs}>Needs one of: {r.capabilityDetail.join(', ')}</p>
              )}
            </div>
          ))}
          <p className={styles.sectionNote}>
            Every gate except partner capability already passes on these. This is the quantified
            business case for finishing an enrolment — take it to the PDM with the audit timeline.
          </p>
        </div>
      )}

      {higherBlocked.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Pays more, but blocked — {higherBlocked.length}</p>
          <p className={styles.sectionNote} style={{ marginBottom: 'var(--space-2)' }}>
            Relevant to these workloads and would pay more than the current recommendation.
          </p>
          {higherBlocked.map((r) => (
            <EngagementRow key={r.key} row={r} fx={fx} />
          ))}
        </div>
      )}

      {blocked.length > 0 && (
        <div className={styles.section}>
          <div className={styles.blockedToggle}>
            <Button size="sm" onClick={() => { setShowBlocked((v) => !v); }}>
              {showBlocked ? 'Hide' : 'Show'} blocked — {realBlocked.length}
              {notRelevant.length > 0 ? ` · ${String(notRelevant.length)} not relevant to these workloads` : ''}
            </Button>
          </div>
          {showBlocked && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              {realBlocked.map((r) => (
                <EngagementRow key={r.key} row={r} fx={fx} />
              ))}
              {notRelevant.length > 0 && <NotRelevantList rows={notRelevant} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
